import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const InvestmentProfileForm = ({ onComplete, onBack, formData, setFormData }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const experienceLevels = [
    {
      value: 'beginner',
      label: 'Beginner',
      description: 'New to investing, learning the basics',
      icon: 'BookOpen'
    },
    {
      value: 'intermediate',
      label: 'Intermediate',
      description: '1-3 years of investing experience',
      icon: 'TrendingUp'
    },
    {
      value: 'advanced',
      label: 'Advanced',
      description: '3+ years, comfortable with complex strategies',
      icon: 'Target'
    },
    {
      value: 'expert',
      label: 'Expert',
      description: 'Professional trader or financial advisor',
      icon: 'Award'
    }
  ];

  const riskTolerances = [
    {
      value: 'conservative',
      label: 'Conservative',
      description: 'Prefer stable, low-risk investments',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      value: 'moderate',
      label: 'Moderate',
      description: 'Balanced approach to risk and reward',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      value: 'aggressive',
      label: 'Aggressive',
      description: 'Comfortable with high-risk, high-reward',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const investmentGoals = [
    { value: 'retirement', label: 'Retirement Planning' },
    { value: 'wealth', label: 'Wealth Building' },
    { value: 'income', label: 'Generate Income' },
    { value: 'education', label: 'Education Funding' },
    { value: 'house', label: 'Buy a Home' },
    { value: 'emergency', label: 'Emergency Fund' },
    { value: 'other', label: 'Other Goals' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.experienceLevel) {
      newErrors.experienceLevel = 'Please select your investment experience level';
    }

    if (!formData.riskTolerance) {
      newErrors.riskTolerance = 'Please select your risk tolerance';
    }

    if (formData.investmentGoals.length === 0) {
      newErrors.investmentGoals = 'Please select at least one investment goal';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        onComplete();
      }, 2000);
    }
  };

  const handleExperienceChange = (value) => {
    setFormData(prev => ({ ...prev, experienceLevel: value }));
    if (errors.experienceLevel) {
      setErrors(prev => ({ ...prev, experienceLevel: '' }));
    }
  };

  const handleRiskToleranceChange = (value) => {
    setFormData(prev => ({ ...prev, riskTolerance: value }));
    if (errors.riskTolerance) {
      setErrors(prev => ({ ...prev, riskTolerance: '' }));
    }
  };

  const handleGoalChange = (goal, checked) => {
    setFormData(prev => ({
      ...prev,
      investmentGoals: checked
        ? [...prev.investmentGoals, goal]
        : prev.investmentGoals.filter(g => g !== goal)
    }));
    if (errors.investmentGoals) {
      setErrors(prev => ({ ...prev, investmentGoals: '' }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="PieChart" size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Investment Profile</h3>
        <p className="text-sm text-muted-foreground">
          Help us personalize your investment experience
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Experience Level */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Investment Experience Level *
            </label>
            {errors.experienceLevel && (
              <p className="text-sm text-destructive mt-1">{errors.experienceLevel}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {experienceLevels.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => handleExperienceChange(level.value)}
                className={`
                  p-4 rounded-lg border-2 text-left transition-all duration-200 ease-out
                  hover:border-primary/50 hover:bg-primary/5
                  ${formData.experienceLevel === level.value
                    ? 'border-primary bg-primary/10' :'border-border bg-card'
                  }
                `}
              >
                <div className="flex items-start space-x-3">
                  <Icon name={level.icon} size={20} className="text-primary mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{level.label}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{level.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Risk Tolerance */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Risk Tolerance *
            </label>
            {errors.riskTolerance && (
              <p className="text-sm text-destructive mt-1">{errors.riskTolerance}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {riskTolerances.map((risk) => (
              <button
                key={risk.value}
                type="button"
                onClick={() => handleRiskToleranceChange(risk.value)}
                className={`
                  p-4 rounded-lg border-2 text-left transition-all duration-200 ease-out
                  hover:border-primary/50 hover:bg-primary/5
                  ${formData.riskTolerance === risk.value
                    ? 'border-primary bg-primary/10' :'border-border bg-card'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${risk.bgColor}`}>
                    <div className={`w-full h-full rounded-full ${risk.color.replace('text-', 'bg-')}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{risk.label}</h4>
                    <p className="text-sm text-muted-foreground">{risk.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Investment Goals */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Investment Goals * (Select all that apply)
            </label>
            {errors.investmentGoals && (
              <p className="text-sm text-destructive mt-1">{errors.investmentGoals}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {investmentGoals.map((goal) => (
              <Checkbox
                key={goal.value}
                label={goal.label}
                checked={formData.investmentGoals.includes(goal.value)}
                onChange={(e) => handleGoalChange(goal.value, e.target.checked)}
                className="p-3 border border-border rounded-lg hover:bg-muted/30"
              />
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            disabled={!formData.experienceLevel || !formData.riskTolerance || formData.investmentGoals.length === 0}
            loading={isSubmitting}
            iconName="Check"
            iconPosition="right"
          >
            {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            fullWidth
            onClick={onBack}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Back to Verification
          </Button>
        </div>
      </form>

      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Lock" size={16} className="text-primary mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Your Data is Secure</p>
            <p className="text-xs text-muted-foreground">
              All information is encrypted and used only to provide personalized investment recommendations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentProfileForm;